import { MantineProvider } from '@mantine/core';
import { ModalsProvider } from '@mantine/modals';
import * as Sentry from "@sentry/browser";
import axios from "axios";
import * as React from "react";
import { createRoot } from 'react-dom/client';
import { Provider } from "react-redux";
import { BrowserRouter } from "react-router-dom";
import { ToastContainer } from 'react-toastify';
import configStore from "./store/configureStore";

import App from "./components/App";
import { TOGGLE_DARK } from "./data/actions/actionTypes";
import { notAuthenticated } from "./data/actions/authenticationActions";
import { getSelf } from "./data/actions/personActions";
import { mantineTheme } from "./theme/mantine-theme";

import "@bjarkehs/react-nice-dates/build/style.css";
import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/dropzone/styles.css';
import "bootstrap/dist/css/bootstrap.css";
import 'react-toastify/dist/ReactToastify.css';

import "./styles/styles.css";

import { prepareFontAwesome } from "./styles/fontawesome";

const store = configStore();
prepareFontAwesome();

axios.interceptors.request.use(
  (config) => {
    config.withCredentials = true;
    return config;
  },
  (error) => {
    const status = error.response ? error.response.status : null;
    if (status === 401) {
      store.dispatch(notAuthenticated());
    }
    return Promise.reject(error);
  },
);

axios.interceptors.response.use(
  (c) => c,
  (error) => {
    // Do something with request error
    const status = error.response ? error.response.status : null;
    if (status === 401) {
      store.dispatch(notAuthenticated());
    }
    return Promise.reject(error);
  },
);

getSelf()(store.dispatch);

window
  .matchMedia("(prefers-color-scheme: dark)")
  .addEventListener("change", (event) => {
    store.dispatch({ type: TOGGLE_DARK, payload: event.matches });
  });

// declare var process: any;

if (!import.meta.env.DEV) {
  Sentry.init({
    dsn: "https://6e15f96daa8d41349399edc1c437035d@o229865.ingest.us.sentry.io/1387676",
    release: import.meta.env.VITE_SENTRY_RELEASE,
    environment: import.meta.env.MODE,
  });
}

const container = document.getElementById("app");
const root = createRoot(container!);

// Create a wrapper component to handle dark mode state
const AppWithMantine = () => {
  const [darkMode, setDarkMode] = React.useState(
    window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
  );

  React.useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => setDarkMode(e.matches);

    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  return (
    <MantineProvider theme={mantineTheme} forceColorScheme={darkMode ? 'dark' : 'light'}>
      <ToastContainer
        position="bottom-center"
        autoClose={4000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme={darkMode ? 'dark' : 'light'}
      />
      <ModalsProvider>
        <Provider store={store}>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </Provider>
      </ModalsProvider>
    </MantineProvider>
  );
};

root.render(<AppWithMantine />);
